import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, GraduationCap, Mail, Calendar, Pencil, Trash2, FileText, Linkedin, Sparkles, BookOpen, ExternalLink, Link as LinkIcon, Link2Off, Check, X, KeyRound, Eye, Copy } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/context/ConfirmContext";
import { startImpersonation } from "@/lib/impersonate";

const BLANK = { name: "", expertise: "", bio: "", mode: "", availability: "", email: "", photo_url: "", bio_url: "", linkedin_url: "", default_meeting_link: "", order: 0 };

function useMentorPhotoUploader(onDone) {
  const [busy, setBusy] = React.useState(false);
  const inputRef = React.useRef(null);
  const trigger = () => inputRef.current?.click();
  const onPick = async (e, mid) => {
    const f = e.target.files?.[0];
    if (!f || !mid) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(`/mentors/${mid}/photo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Headshot uploaded");
      onDone?.(data.photo_url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not upload");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };
  return { inputRef, trigger, onPick, busy };
}

export default function Mentors() {
  const confirm = useConfirm();
  const { user } = useAuth();
  const isAdmin = user && (user.role === "admin" || user.role === "faculty");
  const [mentors, setMentors] = useState([]);
  const [editing, setEditing] = useState(null); // mentor obj or null
  const [form, setForm] = useState(BLANK);
  const [open, setOpen] = useState(false);
  const [enriching, setEnriching] = useState(null); // mentor id while enriching
  const [bulkBusy, setBulkBusy] = useState(false);
  // Inline default-link editor (per mentor card)
  const [linkEditingId, setLinkEditingId] = useState(null);
  const [linkDraft, setLinkDraft] = useState("");
  const [linkBusy, setLinkBusy] = useState(false);
  const [applyingId, setApplyingId] = useState(null);
  // Login-credentials & impersonation state
  const [newLogin, setNewLogin] = useState(null);   // { email, default_password } shown after add
  const [ensuringLogins, setEnsuringLogins] = useState(false);
  const [previewingId, setPreviewingId] = useState(null);

  const ensureLogins = async () => {
    setEnsuringLogins(true);
    try {
      const { data } = await api.post("/admin/mentors/ensure-users");
      if (data.created > 0) {
        toast.success(`Created ${data.created} mentor login account(s)`,
          { description: `Default password: ${data.default_password}` });
      } else {
        toast.message("All mentors already have login accounts", { description: "Nothing to do." });
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create mentor logins");
    } finally { setEnsuringLogins(false); }
  };

  const previewAsMentor = async (m) => {
    if (!m._login_user_id) {
      toast.error("No login account for this mentor — click 'Fix mentor logins' first.");
      return;
    }
    if (!(await confirm({
      title: `Preview the platform as ${m.name}?`,
      description: "You'll be signed in as this mentor. Your admin session is preserved — you can flip back with the yellow banner at the top of every page.",
      confirmText: "Preview as mentor",
    }))) return;
    setPreviewingId(m.id);
    try {
      await startImpersonation(m._login_user_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start preview");
    } finally { setPreviewingId(null); }
  };

  const [resettingId, setResettingId] = useState(null);
  const resetPassword = async (m) => {
    if (!(await confirm({
      title: `Reset ${m.name}'s password to the default?`,
      description: "Their password becomes MentorWeave2026! again and any lockout is cleared. Share it with them and ask them to change it after signing in.",
      confirmText: "Reset password",
    }))) return;
    setResettingId(m.id);
    try {
      const { data } = await api.post(`/admin/mentors/${m.id}/reset-password`);
      toast.success(`Password reset for ${m.name}`, { description: `${data.email} · ${data.default_password}` });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reset password");
    } finally { setResettingId(null); }
  };

  const copyToClipboard = async (text, label = "Copied") => {
    try { await navigator.clipboard.writeText(text); toast.success(label); }
    catch { toast.error("Copy failed — select manually"); }
  };

  const applyDefaultToAllSlots = async (m) => {
    if (!m.default_meeting_link) {
      toast.error("Set the default link first, then apply.");
      return;
    }
    if (!(await confirm({
      title: `Apply ${m.name}'s default link to every one of their slots?`,
      description: "This overwrites the meeting link on every booked + available slot for this mentor with the default. Admins can still override an individual slot afterwards from Mentor Slot Manager.",
      confirmText: "Apply to all slots",
    }))) return;
    setApplyingId(m.id);
    try {
      const { data } = await api.post(`/mentors/${m.id}/apply-default-link-to-slots`);
      toast.success(`${data.updated} slot${data.updated === 1 ? "" : "s"} updated for ${m.name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not apply");
    } finally {
      setApplyingId(null);
    }
  };

  const startLinkEdit = (m) => {
    setLinkEditingId(m.id);
    setLinkDraft(m.default_meeting_link || "");
  };
  const cancelLinkEdit = () => {
    setLinkEditingId(null);
    setLinkDraft("");
  };
  const saveLinkEdit = async (m) => {
    setLinkBusy(true);
    try {
      await api.put(`/mentors/${m.id}`, { default_meeting_link: linkDraft.trim() });
      toast.success(linkDraft.trim() ? `Default link saved for ${m.name}` : `Default link cleared for ${m.name}`);
      cancelLinkEdit();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save link");
    } finally {
      setLinkBusy(false);
    }
  };

  const load = async () => {
    try {
      const { data } = await api.get("/mentors");
      setMentors(data || []);
    } catch (e) {
      toast.error("Could not load mentors");
    }
  };

  const enrichOne = async (m) => {
    setEnriching(m.id);
    try {
      const { data } = await api.post(`/mentors/${m.id}/enrich-from-bio`);
      const fields = (data.updated_fields || []).filter((f) => f !== "updated_at");
      if (fields.length) {
        toast.success(`AI filled: ${fields.join(", ")}`);
      } else {
        toast.message("Nothing to add", { description: "All editable fields already had content." });
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Enrichment failed");
    } finally {
      setEnriching(null);
    }
  };

  const enrichAll = async () => {
    if (!(await confirm({ title: "Run AI bio enrichment across all mentors? Only blank fields will be filled.", destructive: false }))) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post(`/mentors/enrich-all-from-bios`);
      const enriched = (data.results || []).filter((r) => r.status === "enriched").length;
      const skipped = (data.results || []).filter((r) => r.status !== "enriched").length;
      toast.success(`Enriched ${enriched} mentor(s)`, { description: `${skipped} skipped (no new fields or unreadable bio).` });
      load();
    } catch (e) {
      toast.error("Bulk enrichment failed");
    } finally {
      setBulkBusy(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startCreate = () => {
    setEditing(null); setForm(BLANK); setOpen(true);
  };
  const startEdit = (m) => {
    setEditing(m);
    setForm({ ...BLANK, ...m });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    try {
      if (editing) {
        await api.put(`/mentors/${editing.id}`, form);
        toast.success("Mentor updated");
        setOpen(false); load();
      } else {
        const { data } = await api.post("/mentors", form);
        toast.success("Mentor added");
        setOpen(false); load();
        // Surface the login credentials so admin can share them.
        if (data?.login?.created && data.login.default_password) {
          setNewLogin({ name: data.name, email: data.login.email, password: data.login.default_password });
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (m) => {
    if (!(await confirm({ title: `Remove ${m.name} from the mentor directory?`, destructive: true }))) return;
    try {
      await api.delete(`/mentors/${m.id}`);
      toast.success("Mentor removed");
      load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="space-y-8" data-testid="mentors-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-eyebrow">Mentors</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter mt-1">Who&apos;s in the room.</h1>
          <p className="text-sm text-[#525252] max-w-2xl mt-2">
            Programme mentors available across the week. See expertise, preferred mode and slot availability.
            Faculty maintains this directory.
          </p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2 flex-wrap">
            <Button onClick={ensureLogins} disabled={ensuringLogins} variant="outline"
                    className="rounded-none border-[#0F1F2E] text-[#0F1F2E] hover:bg-[#0F1F2E] hover:text-white uppercase tracking-wide text-xs font-semibold"
                    data-testid="mentors-ensure-logins-btn">
              <KeyRound size={14} className="mr-2" /> {ensuringLogins ? "Fixing…" : "Fix mentor logins"}
            </Button>
            <Button onClick={enrichAll} disabled={bulkBusy} variant="outline"
                    className="rounded-none border-[#2596be] text-[#2596be] hover:bg-[#E6F4FA] uppercase tracking-wide text-xs font-semibold"
                    data-testid="mentors-enrich-all-btn">
              <Sparkles size={14} className="mr-2" /> {bulkBusy ? "Enriching…" : "AI fill from bios"}
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button onClick={startCreate}
                        className="rounded-none bg-[#2596be] hover:bg-[#1e7da3] uppercase tracking-wide text-xs font-semibold"
                        data-testid="mentor-add-btn">
                  <Plus size={14} className="mr-2" /> Add mentor
                </Button>
              </DialogTrigger>
            <DialogContent className="rounded-none max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
              <DialogHeader>
                <DialogTitle className="font-display tracking-tight">
                  {editing ? "Edit mentor" : "Add mentor"}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3 overflow-y-auto pr-1 flex-1">
                <Field label="Name *" value={form.name} set={(v) => setForm((f) => ({ ...f, name: v }))} testid="mentor-name-input" />
                <Field label="Expertise" value={form.expertise} set={(v) => setForm((f) => ({ ...f, expertise: v }))}
                       placeholder="e.g. M&A integration, post-merger ops" testid="mentor-expertise-input" />
                <Field label="Preferred mode" value={form.mode} set={(v) => setForm((f) => ({ ...f, mode: v }))}
                       placeholder="In-person · Video · Async (Slack)" testid="mentor-mode-input" />
                <Field label="Availability" value={form.availability} set={(v) => setForm((f) => ({ ...f, availability: v }))}
                       placeholder="e.g. Tue/Thu 14:00–16:00 UK" testid="mentor-availability-input" />
                <Field label="Contact email" value={form.email} set={(v) => setForm((f) => ({ ...f, email: v }))}
                       placeholder="optional" testid="mentor-email-input" />
                <div>
                  <Label className="text-xs text-[#525252]">Photo</Label>
                  <div className="mt-2 flex items-center gap-3">
                    {form.photo_url && (
                      <img
                        src={form.photo_url}
                        alt="preview"
                        className="w-14 h-14 object-cover border border-[#E5E7EB] shrink-0"
                        data-testid="mentor-photo-preview"
                      />
                    )}
                    <div className="flex-1 space-y-2">
                      <Input
                        value={form.photo_url}
                        onChange={(e) => setForm((f) => ({ ...f, photo_url: e.target.value }))}
                        placeholder="Paste an image URL, or upload below"
                        className="rounded-none h-10"
                        data-testid="mentor-photo-input"
                      />
                      {editing?.id && (
                        <PhotoUploadButton mentorId={editing.id}
                          onUploaded={(url) => setForm((f) => ({ ...f, photo_url: url }))} />
                      )}
                      {!editing?.id && (
                        <p className="text-[10px] text-[#525252]">Save the mentor first, then reopen to upload a headshot from your computer.</p>
                      )}
                    </div>
                  </div>
                </div>
                <Field label="Bio PDF / link" value={form.bio_url} set={(v) => setForm((f) => ({ ...f, bio_url: v }))}
                       placeholder="Dropbox / Drive PDF link" testid="mentor-bio-url-input" />
                <Field label="LinkedIn" value={form.linkedin_url} set={(v) => setForm((f) => ({ ...f, linkedin_url: v }))}
                       placeholder="https://linkedin.com/in/…" testid="mentor-linkedin-input" />
                <div className="border border-[#2596be] bg-[#E6F4FA] p-3">
                  <Label className="text-xs text-[#0A0A0A] font-semibold uppercase tracking-wider">Default meeting link</Label>
                  <Input
                    value={form.default_meeting_link || ""}
                    onChange={(e) => setForm((f) => ({ ...f, default_meeting_link: e.target.value }))}
                    placeholder="https://zoom.us/j/…  ·  https://meet.google.com/…  ·  https://teams.microsoft.com/l/…"
                    className="rounded-none mt-2 h-10"
                    data-testid="mentor-default-link-input"
                  />
                  <p className="text-[11px] text-[#0A0A0A] mt-2 leading-relaxed">
                    <strong>Used for every slot this mentor is booked into</strong>, unless the admin sets a slot-specific override on the Mentor Slot Manager. Mentors can also update this themselves from their own dashboard. Leave blank if the mentor wants to share a fresh link per session.
                  </p>
                </div>
                <div>
                  <Label className="text-xs text-[#525252]">Bio</Label>
                  <Textarea
                    value={form.bio}
                    onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value }))}
                    placeholder="Background, current role, signature topics."
                    className="rounded-none mt-1 min-h-[100px]"
                    data-testid="mentor-bio-input"
                  />
                </div>
                <div>
                  <Label className="text-xs text-[#525252]">Display order</Label>
                  <Input
                    type="number"
                    value={form.order}
                    onChange={(e) => setForm((f) => ({ ...f, order: Number(e.target.value) || 0 }))}
                    className="rounded-none mt-1 h-10 w-32"
                    data-testid="mentor-order-input"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}
                        className="rounded-none border-[#0A0A0A]" data-testid="mentor-cancel-btn">Cancel</Button>
                <Button onClick={save}
                        className="rounded-none bg-[#2596be] hover:bg-[#1e7da3] uppercase tracking-wide text-xs font-semibold"
                        data-testid="mentor-save-btn">
                  {editing ? "Update" : "Add"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        )}
      </header>

      {/* Get the best from your mentor — guide */}
      <section
        className="border-2 border-[#0A0A0A] bg-[#FEF3D0] p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-5"
        data-testid="get-the-best-from-mentor-card"
      >
        <div className="shrink-0 w-12 h-12 bg-[#0A0A0A] text-[#f4b847] flex items-center justify-center">
          <BookOpen size={22} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="label-eyebrow text-[#7A5B17]">Make the most of every slot</div>
          <h2 className="font-display text-2xl font-black tracking-tighter mt-1 text-[#0A0A0A]">
            Get the best from your mentor
          </h2>
          <p className="text-sm text-[#0A0A0A] mt-2 leading-relaxed max-w-2xl">
            Mentors give you 30 minutes at a time — arrive prepared, lead the conversation, and follow up.
            Your team has one assigned mentor for the whole programme; book their slots from Session 2 onwards.
            Read the short Weave guide before your first booking.
          </p>
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          <a
            href="https://www.dropbox.com/scl/fi/a7rn9337xx8fid3cc5cfb/Day-1-Get-the-Best-from-the-Mentors.pdf?rlkey=cm9r6i46sks5qg3dlrzjd82fq&dl=0"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#0A0A0A] text-[#f4b847] px-4 py-3 text-xs font-bold uppercase tracking-wider hover:bg-[#1a1a1a] transition-colors"
            data-testid="get-the-best-from-mentor-pdf-link"
          >
            <FileText size={14} /> Read the guide (PDF)
          </a>
          <a
            href="/mentor-booking"
            className="inline-flex items-center gap-2 border border-[#0A0A0A] px-4 py-3 text-xs font-bold uppercase tracking-wider hover:bg-[#0A0A0A] hover:text-white transition-colors"
            data-testid="get-the-best-from-mentor-book-link"
          >
            <ExternalLink size={14} /> Book time with your mentor
          </a>
        </div>
      </section>

      {mentors.length === 0 ? (
        <div className="border-2 border-dashed border-[#E5E7EB] p-10 text-center">
          <GraduationCap size={28} className="mx-auto text-[#525252]" />
          <div className="mt-3 font-medium text-[#0A0A0A]">No mentors listed yet</div>
          <p className="mt-1 text-xs text-[#525252]">
            {isAdmin ? "Click 'Add mentor' to start the directory." : "Faculty hasn't published the mentor directory yet."}
          </p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {mentors.map((m) => (
            <article key={m.id} className="border border-[#E5E7EB] bg-white p-5 flex flex-col gap-3"
                     data-testid={`mentor-card-${m.id}`}>
              <header className="flex items-start gap-3">
                {m.photo_url ? (
                  <img src={m.photo_url} alt={m.name} className="w-14 h-14 object-cover bg-[#F3F4F6]" />
                ) : (
                  <div className="w-14 h-14 bg-[#2596be] text-white flex items-center justify-center text-lg font-bold uppercase">
                    {(m.name || "?").slice(0, 2)}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-display text-lg font-bold tracking-tight text-[#0A0A0A]" data-testid={`mentor-name-${m.id}`}>
                    {m.name}
                  </div>
                  {m.expertise && (
                    <div className="text-xs text-[#2596be] uppercase tracking-wider mt-0.5">{m.expertise}</div>
                  )}
                </div>
              </header>
              {m.bio && <p className="text-sm text-[#525252] leading-relaxed">{m.bio}</p>}
              <div className="space-y-1 text-xs text-[#0A0A0A]">
                {m.mode && <div className="flex items-start gap-2"><span className="label-eyebrow w-20 shrink-0">Mode</span><span>{m.mode}</span></div>}
                {m.availability && <div className="flex items-start gap-2"><Calendar size={12} className="text-[#525252] mt-0.5" /><span>{m.availability}</span></div>}
                {m.email && <div className="flex items-start gap-2"><Mail size={12} className="text-[#525252] mt-0.5" /><a className="text-[#2596be] hover:underline" href={`mailto:${m.email}`}>{m.email}</a></div>}
              </div>
              {m.bio_url && (
                <a href={m.bio_url} target="_blank" rel="noopener noreferrer"
                   data-testid={`mentor-bio-link-${m.id}`}
                   className="inline-flex items-center gap-2 text-[11px] uppercase tracking-wider text-[#2596be] font-semibold hover:text-[#1e7da3]">
                  <FileText size={12} /> Download bio
                </a>
              )}
              {m.linkedin_url && (
                <a href={m.linkedin_url} target="_blank" rel="noopener noreferrer"
                   data-testid={`mentor-linkedin-${m.id}`}
                   className="inline-flex items-center gap-2 text-[11px] uppercase tracking-wider text-[#2596be] font-semibold hover:text-[#1e7da3]">
                  <Linkedin size={12} /> LinkedIn
                </a>
              )}
              {/* Default meeting link — admin inline editor + status */}
              {isAdmin && (
                <div className="border border-[#E5E7EB] bg-[#FAFAFA] p-3 mt-1" data-testid={`mentor-default-link-${m.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="label-eyebrow !text-[#525252]">Default meeting link</div>
                    {linkEditingId !== m.id && (
                      <button
                        onClick={() => startLinkEdit(m)}
                        className="text-[10px] uppercase tracking-wider text-[#2596be] hover:text-[#1e7da3] font-semibold inline-flex items-center gap-1"
                        data-testid={`mentor-default-link-edit-${m.id}`}
                      >
                        <Pencil size={10} /> {m.default_meeting_link ? "Change" : "Set"}
                      </button>
                    )}
                  </div>
                  {linkEditingId === m.id ? (
                    <div className="mt-2 space-y-2">
                      <Input
                        value={linkDraft}
                        onChange={(e) => setLinkDraft(e.target.value)}
                        placeholder="https://zoom.us/j/…"
                        className="rounded-none h-9 text-sm"
                        autoFocus
                        data-testid={`mentor-default-link-inline-input-${m.id}`}
                      />
                      <div className="flex items-center gap-2">
                        <Button
                          onClick={() => saveLinkEdit(m)}
                          disabled={linkBusy}
                          className="h-8 rounded-none bg-[#2596be] hover:bg-[#1e7da3] text-[11px] uppercase tracking-wide"
                          data-testid={`mentor-default-link-save-${m.id}`}
                        >
                          <Check size={11} className="mr-1.5" /> {linkBusy ? "Saving…" : "Save"}
                        </Button>
                        <Button
                          onClick={cancelLinkEdit}
                          variant="outline"
                          className="h-8 rounded-none border-[#0A0A0A] text-[11px] uppercase tracking-wide"
                          data-testid={`mentor-default-link-cancel-${m.id}`}
                        >
                          <X size={11} className="mr-1.5" /> Cancel
                        </Button>
                      </div>
                      <p className="text-[10px] text-[#525252] leading-relaxed">
                        Saved here = used for every slot. Admin can still override per slot on <em>Mentor Slot Manager</em>. Mentor can also edit this from their own dashboard.
                      </p>
                    </div>
                  ) : (
                    <>
                      {m.default_meeting_link ? (
                        <a
                          href={m.default_meeting_link}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs text-[#16A34A] inline-flex items-center gap-1 mt-1 hover:underline break-all"
                          data-testid={`mentor-default-link-value-${m.id}`}
                        >
                          <LinkIcon size={11} /> {m.default_meeting_link}
                        </a>
                      ) : (
                        <div className="text-xs text-[#A23232] inline-flex items-center gap-1 mt-1">
                          <Link2Off size={11} /> No default set — teams will see &quot;No link yet&quot; unless admin sets a slot-specific link.
                        </div>
                      )}
                      <p className="text-[10px] text-[#525252] mt-1 leading-relaxed">
                        This link is used by default for every slot. Admin can override per slot on Mentor Slot Manager; mentor can also edit this from their own dashboard.
                      </p>
                      {m.default_meeting_link && (
                        <Button
                          onClick={() => applyDefaultToAllSlots(m)}
                          disabled={applyingId === m.id}
                          variant="outline"
                          className="h-7 rounded-none border-[#2596be] text-[#2596be] hover:bg-[#E6F4FA] text-[10px] uppercase tracking-wide mt-2"
                          data-testid={`mentor-default-link-apply-${m.id}`}
                        >
                          <LinkIcon size={10} className="mr-1.5" />
                          {applyingId === m.id ? "Applying…" : "Apply to all my slots"}
                        </Button>
                      )}
                    </>
                  )}
                </div>
              )}
              {isAdmin && (
                <div className="border border-[#25AAAA] bg-[#E6F5F5] p-3 space-y-2" data-testid={`mentor-login-${m.id}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="min-w-0">
                      <div className="label-eyebrow !text-[#0F5A5A]">Mentor login</div>
                      {m._login_user_id ? (
                        <button
                          onClick={() => copyToClipboard(m._login_email, "Email copied")}
                          className="text-xs text-[#0F5A5A] inline-flex items-center gap-1 mt-1 hover:underline break-all"
                          data-testid={`mentor-login-email-${m.id}`}
                        >
                          <Mail size={11} /> {m._login_email} <Copy size={10} />
                        </button>
                      ) : (
                        <div className="text-xs text-[#A23232] inline-flex items-center gap-1 mt-1">
                          <Link2Off size={11} /> No login account — click <em>Fix mentor logins</em>.
                        </div>
                      )}
                      {m._login_user_id && !m._login_password_set && (
                        <div className="text-[10px] text-[#525252] mt-1">
                          Default password: <code className="bg-white px-1 border border-[#25AAAA]">MentorWeave2026!</code>
                          <button
                            onClick={() => copyToClipboard("MentorWeave2026!", "Password copied")}
                            className="ml-2 text-[#0F5A5A] hover:underline"
                            data-testid={`mentor-login-copy-pw-${m.id}`}
                          >Copy</button>
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col gap-1.5 items-stretch">
                      <Button
                        onClick={() => previewAsMentor(m)}
                        disabled={previewingId === m.id || !m._login_user_id}
                        className="h-8 rounded-none bg-[#25AAAA] hover:bg-[#1e7d7d] text-[11px] uppercase tracking-wide font-semibold"
                        data-testid={`mentor-preview-as-${m.id}`}
                      >
                        <Eye size={12} className="mr-1.5" /> {previewingId === m.id ? "Loading…" : "Preview as mentor"}
                      </Button>
                      <Button
                        onClick={() => resetPassword(m)}
                        disabled={resettingId === m.id}
                        variant="outline"
                        className="h-8 rounded-none border-[#0F5A5A] text-[#0F5A5A] hover:bg-white text-[11px] uppercase tracking-wide font-semibold"
                        data-testid={`mentor-reset-password-${m.id}`}
                      >
                        <KeyRound size={12} className="mr-1.5" /> {resettingId === m.id ? "Resetting…" : "Reset to default password"}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              {isAdmin && (
                <div className="flex gap-2 mt-2 border-t border-[#E5E7EB] pt-3">
                  {m.bio_url && (
                    <Button onClick={() => enrichOne(m)} disabled={enriching === m.id} variant="outline"
                            className="h-8 rounded-none border-[#2596be] text-[#2596be] hover:bg-[#E6F4FA] text-[11px] uppercase tracking-wide"
                            data-testid={`mentor-enrich-${m.id}`}>
                      <Sparkles size={12} className="mr-1.5" /> {enriching === m.id ? "…" : "AI fill"}
                    </Button>
                  )}
                  <Button onClick={() => startEdit(m)} variant="outline"
                          className="h-8 rounded-none border-[#0A0A0A] text-[11px] uppercase tracking-wide"
                          data-testid={`mentor-edit-${m.id}`}>
                    <Pencil size={12} className="mr-1.5" /> Edit
                  </Button>
                  <Button onClick={() => remove(m)} variant="outline"
                          className="h-8 rounded-none border-[#e93e36] text-[#e93e36] hover:bg-[#FDEDED] text-[11px] uppercase tracking-wide"
                          data-testid={`mentor-delete-${m.id}`}>
                    <Trash2 size={12} className="mr-1.5" /> Remove
                  </Button>
                </div>
              )}
            </article>
          ))}
        </div>
      )}

      {/* Post-create login credentials dialog */}
      <Dialog open={!!newLogin} onOpenChange={(o) => !o && setNewLogin(null)}>
        <DialogContent className="rounded-none max-w-md" data-testid="mentor-new-login-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight flex items-center gap-2">
              <KeyRound size={18} className="text-[#25AAAA]" /> Mentor login created
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-[#525252]">
              Share these credentials with <strong className="text-[#0A0A0A]">{newLogin?.name}</strong>.
              They can change the password on their dashboard after first login.
            </p>
            <div className="border border-[#25AAAA] bg-[#E6F5F5] p-4 space-y-2">
              <div>
                <div className="label-eyebrow !text-[#0F5A5A]">Email</div>
                <div className="flex items-center justify-between gap-2 mt-1">
                  <code className="text-[13px] break-all" data-testid="mentor-new-login-email">{newLogin?.email}</code>
                  <button onClick={() => copyToClipboard(newLogin.email, "Email copied")}
                          className="text-xs text-[#0F5A5A] hover:underline inline-flex items-center gap-1">
                    <Copy size={11} /> Copy
                  </button>
                </div>
              </div>
              <div>
                <div className="label-eyebrow !text-[#0F5A5A]">Default password</div>
                <div className="flex items-center justify-between gap-2 mt-1">
                  <code className="text-[13px] font-mono" data-testid="mentor-new-login-password">{newLogin?.password}</code>
                  <button onClick={() => copyToClipboard(newLogin.password, "Password copied")}
                          className="text-xs text-[#0F5A5A] hover:underline inline-flex items-center gap-1">
                    <Copy size={11} /> Copy
                  </button>
                </div>
              </div>
            </div>
            <p className="text-[11px] text-[#525252] italic">
              This dialog appears only once — you can always copy the email from the mentor card, and the
              default password is <code>MentorWeave2026!</code> until the mentor changes it.
            </p>
          </div>
          <DialogFooter>
            <Button onClick={() => setNewLogin(null)} className="rounded-none bg-[#25AAAA] hover:bg-[#1e7d7d]"
                    data-testid="mentor-new-login-close">Got it</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, value, set, placeholder, testid }) {
  return (
    <div>
      <Label className="text-xs text-[#525252]">{label}</Label>
      <Input
        value={value || ""}
        onChange={(e) => set(e.target.value)}
        placeholder={placeholder}
        className="rounded-none mt-1 h-10"
        data-testid={testid}
      />
    </div>
  );
}

function PhotoUploadButton({ mentorId, onUploaded }) {
  const { inputRef, trigger, onPick, busy } = useMentorPhotoUploader(onUploaded);
  return (
    <div>
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif"
             className="hidden" onChange={(e) => onPick(e, mentorId)}
             data-testid="mentor-photo-file-input" />
      <button type="button" onClick={trigger} disabled={busy}
              data-testid="mentor-photo-upload-btn"
              className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider border border-[#0A0A0A] bg-[#0A0A0A] text-white px-3 py-1.5 hover:bg-[#1a1a1a] disabled:opacity-50">
        {busy ? "Uploading…" : "Upload headshot from computer"}
      </button>
    </div>
  );
}

